/**
 * Helper utility to format company names for display
 * 
 * The API typically returns company names in the format "Arabic - English"
 * (e.g., "فستق الخليج - FUSTUQ KHALEEJ"), but for invoice displays,
 * we need to show them as "English - Arabic" (e.g., "FUSTUQ KHALEEJ - فستق الخليج")
 */

/**
 * Reverses the company name format from "Arabic - English" to "English - Arabic"
 * 
 * @param {string} companyName - The company name string to reverse
 * @returns {string} - The reversed company name or original if no separator found
 * 
 * @example
 * reverseCompanyNameFormat("فستق الخليج - FUSTUQ KHALEEJ")
 * // Returns: "FUSTUQ KHALEEJ - فستق الخليج"
 */
export const reverseCompanyNameFormat = (companyName) => {
  if (!companyName || typeof companyName !== 'string') {
    return companyName || '';
  }

  // Check if the name contains a separator (dash with spaces around it)
  const separatorPattern = /\s*-\s*/;
  const parts = companyName.split(separatorPattern);

  // Only reverse if we have exactly 2 parts (Arabic and English)
  if (parts.length === 2) {
    const [part1, part2] = parts;
    // Reverse the order: part2 - part1
    return `${part2.trim()} - ${part1.trim()}`;
  }

  // Return original name if no separator or wrong format
  return companyName;
};

/**
 * Gets the formatted company name for display, handling alternativeCompanyName if present
 * 
 * @param {object} companyDetails - Company details object containing companyName and alternativeCompanyName
 * @param {boolean} reverseFormat - Whether to reverse the format (default: false)
 * @returns {string} - The formatted company name
 */
export const getFormattedCompanyName = (companyDetails, reverseFormat = false) => {
  if (!companyDetails) {
    return '';
  }

  const companyName = companyDetails.companyName || companyDetails.name || '';
  const alternativeCompanyName = companyDetails.alternativeCompanyName || '';

  // If there's an alternative company name, combine them
  let displayName = companyName;
  if (alternativeCompanyName) {
    displayName = `${companyName} - ${alternativeCompanyName}`;
  }

  // Apply reverse formatting if requested
  if (reverseFormat) {
    return reverseCompanyNameFormat(displayName);
  }

  return displayName;
};
